import { useEffect, useRef, useState } from 'react'
import { SliderContainer, SliderRange, SliderThumb, SliderTrack } from './Slider.styled'

interface SliderProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  progress?: number
  trackColor?: string
  rangeColor?: string
  min?: number
  max?: number
  step?: number
  disabled?: boolean
  onChange?: (value: number) => void
}

export const Slider: React.FC<SliderProps> = ({
  progress = 0,
  trackColor = '#F3F4F6',
  rangeColor = '#4D4BE7',
  onChange,
  min = 0,
  max = 100,
  step = 1,
  disabled = false,
  ...props
}: SliderProps) => {
  const [currentProgress, setCurrentProgress] = useState<number>(progress)
  const trackRef = useRef<HTMLDivElement>(null)
  const isDragging = useRef<boolean>(false)

  useEffect(() => {
    setCurrentProgress(progress)
  }, [progress])

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (disabled) return
    e.preventDefault()
    e.stopPropagation()
    isDragging.current = true
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    updateProgressFromMouseEvent(e)
  }

  const handleMouseMove = (e: MouseEvent): void => {
    if (isDragging.current) {
      updateProgressFromMouseEvent(e)
    }
  }

  const handleMouseUp = (): void => {
    isDragging.current = false
    document.removeEventListener('mousemove', handleMouseMove)
    document.removeEventListener('mouseup', handleMouseUp)
  }

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>): void => {
    if (disabled) return
    isDragging.current = true
    document.addEventListener('touchmove', handleTouchMove, { passive: false })
    document.addEventListener('touchend', handleTouchEnd)
    updateProgressFromTouchEvent(e)
  }

  const handleTouchMove = (e: TouchEvent): void => {
    if (isDragging.current) {
      e.preventDefault()
      updateProgressFromTouchEvent(e)
    }
  }

  const handleTouchEnd = (): void => {
    isDragging.current = false
    document.removeEventListener('touchmove', handleTouchMove)
    document.removeEventListener('touchend', handleTouchEnd)
  }

  const updateProgressFromMouseEvent = (e: MouseEvent | React.MouseEvent): void => {
    if (trackRef.current) {
      const rect = trackRef.current.getBoundingClientRect()
      const offsetX = e.clientX - rect.left
      updateProgress(offsetX, rect.width)
    }
  }

  const updateProgressFromTouchEvent = (e: TouchEvent | React.TouchEvent): void => {
    if (trackRef.current && e.touches.length > 0) {
      const rect = trackRef.current.getBoundingClientRect()
      const touch = e.touches[0]
      const offsetX = touch.clientX - rect.left
      updateProgress(offsetX, rect.width)
    }
  }

  const updateProgress = (offsetX: number, width: number): void => {
    let newProgress = (offsetX / width) * 100
    newProgress = Math.max(0, Math.min(100, newProgress))

    if (step > 0) {
      const stepsInRange = (max - min) / step
      const progressPerStep = 100 / stepsInRange
      newProgress = Math.round(newProgress / progressPerStep) * progressPerStep
    }

    setCurrentProgress(newProgress)

    if (onChange) {
      let value = min + (newProgress / 100) * (max - min)
      if (step > 0) {
        value = Math.round(value / step) * step
      }
      if (step >= 1) {
        value = Math.round(value)
      }
      onChange(value)
    }
  }

  return (
    <SliderContainer {...props} disabled={disabled}>
      <SliderTrack
        ref={trackRef}
        trackColor={trackColor}
        disabled={disabled}
        onClick={(e: React.MouseEvent<HTMLDivElement>) => {
          if (!disabled) {
            updateProgressFromMouseEvent(e)
          }
        }}
      >
        <SliderRange progress={currentProgress} rangeColor={rangeColor} disabled={disabled} />
      </SliderTrack>
      <SliderThumb
        progress={currentProgress}
        thumbBorderColor={rangeColor}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        tabIndex={disabled ? -1 : 0}
        role="slider"
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={min + (currentProgress / 100) * (max - min)}
        aria-disabled={disabled}
        disabled={disabled}
      />
    </SliderContainer>
  )
}
